/**
 * landing.js - "Arcade Road World"
 *
 * Everything decorative is drawn from the GAME'S OWN sprite atlas
 * (src/render/Sprites.js) so the landing and the game stay one brand.
 * The game itself (play.html) is embedded untouched.
 *
 * Modules: i18n, +18 age gate, cookie bar, sprite injection, hero road
 * canvas, nav, FAQ accordion, difficulty selector, trailer autoplay,
 * iframe loader, fullscreen mobile launcher, scroll reveals.
 */
import { buildSprites } from '../render/Sprites.js';
import { getLang, setLang } from '../ui/i18n.js';
import { CONFIG, AFFILIATE_LINK } from '../config.js';

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ================= i18n ================= */

const I18N = {
  title: { ar: 'Chicken Road | جرّب الديمو مجاناً', en: 'Chicken Road | Try the free demo' },
  metaDesc: {
    ar: 'لعبة Chicken Road: اعبر بالدجاجة خطاً بعد خط، كل خط يرفع مضاعفك، واسحب أرباحك قبل مرور السيارة. جرّب النسخة التجريبية مجاناً من دون تسجيل.',
    en: 'Chicken Road: cross lane by lane, every lane raises your multiplier, and cash out before a car gets there. Try the demo free, no sign-up.',
  },

  ageTitle: { ar: 'التحقق من العمر (+18)', en: 'Age Verification (18+)' },
  ageLabel: { ar: 'أدخل سنة ميلادك', en: 'Enter your birth year' },
  ageVerify: { ar: 'تحقق', en: 'Verify' },
  ageInvalid: { ar: 'الرجاء إدخال سنة صحيحة.', en: 'Please enter a valid year.' },
  ageDenied: { ar: 'عذراً، هذا الموقع للبالغين فوق 18 عاماً فقط.', en: 'Sorry, this site is for adults 18+ only.' },

  cookieText: { ar: 'نستخدم ملفات تعريف الارتباط لتحسين تجربتك.', en: 'We use cookies for a better user experience.' },
  cookiePolicy: { ar: 'سياسة الخصوصية', en: 'Privacy Policy' },
  cookieAccept: { ar: 'قبول الكل', en: 'Accept all' },
  cookieReject: { ar: 'رفض الكل', en: 'Reject all' },

  navAria: { ar: 'روابط الصفحة', en: 'Page links' },
  menuAria: { ar: 'القائمة', en: 'Menu' },
  navGame: { ar: 'اللعبة', en: 'The game' },
  navHow: { ar: 'كيف تلعب', en: 'How to play' },
  navLevels: { ar: 'المستويات', en: 'Levels' },
  navFaq: { ar: 'أسئلة', en: 'FAQ' },
  ctaReal: { ar: 'العب بمال حقيقي', en: 'Play for real money' },
  ctaDemo: { ar: 'جرّب الديمو مجاناً', en: 'Try the free demo' },

  heroTitle: {
    ar: 'اعبر الطريق…<br /><span class="hl hl-squig">واسحب<svg class="squig" viewBox="0 0 141 6" fill="none" aria-hidden="true"><path d="M1 5C22.8731 2.55684 81.2954 -1.30336 140 2.80115" stroke="currentColor"/></svg></span> قبل وصول السيارة!',
    en: 'Cross the road…<br /><span class="hl hl-squig">cash out<svg class="squig" viewBox="0 0 141 6" fill="none" aria-hidden="true"><path d="M1 5C22.8731 2.55684 81.2954 -1.30336 140 2.80115" stroke="currentColor"/></svg></span> in time!',
  },
  heroKicker: {
    ar: 'لعبة كراش سريعة بقرارات حقيقية',
    en: 'A fast crash game of real decisions',
  },
  statRtp: { ar: 'نسبة الإرجاع للاعبين', en: 'Return to player' },
  statLevels: { ar: 'مستويات صعوبة', en: 'Difficulty levels' },
  statMax: { ar: 'أقصى مضاعف (صعب)', en: 'Top multiplier (hard)' },
  spinBadge: {
    ar: 'العب الآن • بدون تسجيل • العب الآن • بدون تسجيل • ',
    en: 'PLAY NOW • NO SIGN-UP • PLAY NOW • NO SIGN-UP • ',
  },
  chipWin: { ar: 'ربح', en: 'Win' },
  chipMult: { ar: 'مضاعف', en: 'Multiplier' },
  chipInstant: { ar: 'سحب فوري', en: 'Instant cash-out' },
  tgKicker: { ar: 'قناتنا الرسمية', en: 'Our official channel' },
  tgTitle: { ar: 'تابعنا على تيليجرام', en: 'Follow us on Telegram' },
  tgSub: {
    ar: 'كل جديد يصلك أولاً بأول: عروض حصرية، مكافآت، وأخبار اللعبة.',
    en: 'Be the first to know: exclusive offers, bonuses, and game news.',
  },
  tgCta: { ar: 'انضم إلى القناة', en: 'Join the channel' },
  tgFooter: { ar: 'قناتنا على تيليجرام', en: 'Our Telegram channel' },
  bonusTag: { ar: 'مكافأة الشريك', en: 'Partner bonus' },
  bonusTitle: {
    ar: 'احصل على مكافأة <span class="bonus-amount" dir="ltr">$100</span> عند التسجيل',
    en: 'Get a <span class="bonus-amount" dir="ltr">$100</span> bonus when you sign up',
  },
  bonusSub: {
    ar: 'مكافأة ترحيبية من منصّة الشريك عند إنشاء حسابك. تُطبّق شروط العرض.',
    en: 'A welcome bonus from our partner platform when you create your account. Offer terms apply.',
  },
  bonusCta: { ar: 'أنشئ حسابك واحصل على المكافأة', en: 'Create your account & claim the bonus' },
  bonusTerms: { ar: 'اقرأ شروط العرض', en: 'Read the offer terms' },
  bridgeTitle: { ar: 'أنت على وشك الانتقال إلى منصّة الشريك', en: 'You’re about to head to our partner platform' },
  bridgeSub: { ar: 'خطوة واحدة تفصلك عن إنشاء حسابك واستلام مكافأتك.', en: 'One step away from creating your account and claiming your bonus.' },
  bridgeStep1: { ar: 'أنشئ حسابك في دقيقة', en: 'Create your account in a minute' },
  bridgeStep2: { ar: 'فعّل مكافأتك', en: 'Activate your bonus' },
  bridgeStep3: { ar: 'ابدأ اللعب بمال حقيقي', en: 'Start playing for real money' },
  bridgeContinue: { ar: 'متابعة إلى التسجيل', en: 'Continue to sign up' },
  bridgeBack: { ar: 'رجوع', en: 'Back' },
  bridgeTerms: { ar: 'تُطبّق شروط العرض. اللعب بمال حقيقي للبالغين (+18) فقط.', en: 'Offer terms apply. Real-money play is for adults (18+) only.' },
  heroSub: {
    ar: 'اختر مستوى الصعوبة، تقدّم خطوة بخطوة، واسحب مضاعفك في اللحظة المناسبة.',
    en: 'Pick a difficulty, advance step by step, and cash out your multiplier at the right moment.',
  },
  chipFree: { ar: 'ديمو مجاني', en: 'Free demo' },
  chipNoReg: { ar: 'بدون تسجيل', en: 'No sign-up' },
  chipLevels: { ar: 'أربعة مستويات', en: 'Four levels' },
  chipCash: { ar: 'سحب في أي لحظة', en: 'Cash out any time' },
  heroAlt: {
    ar: 'رسم من اللعبة: الدجاجة تقفز فوق أغطية المضاعفات',
    en: 'Game art: the chicken hopping over multiplier manholes',
  },

  demoTitle: { ar: 'جرّب اللعبة الآن، من دون تسجيل', en: 'Play the demo now, no sign-up' },
  demoSub: {
    ar: 'رصيد تجريبي بقيمة 10,000$ يتجدد تلقائياً. اللعبة نفسها والأرقام نفسها، من دون أي مخاطرة.',
    en: 'A 10,000$ practice balance that refills itself. Same game, same numbers, zero risk.',
  },
  liveDemo: { ar: 'ديمو مباشر', en: 'Live demo' },
  loadingRoad: { ar: 'جارٍ تجهيز الطريق…', en: 'Preparing the road…' },
  tagBalance: { ar: 'رصيد تجريبي', en: 'Practice balance' },
  demoFullscreen: { ar: 'افتح اللعبة بملء الشاشة', en: 'Open the game full screen' },
  iframeTitle: { ar: 'لعبة Chicken Road التجريبية', en: 'Chicken Road demo game' },
  playNow: { ar: 'ابدأ اللعب', en: 'Start playing' },
  glNote: { ar: 'تفتح اللعبة بملء الشاشة', en: 'Opens full screen' },
  back: { ar: 'رجوع', en: 'Back' },
  rotateHint: { ar: 'لفّ جهازك للوضع الأفقي لتجربة أوسع', en: 'Rotate your device for a wider view' },

  trailerTitle: { ar: 'شاهد اللعبة بالحركة', en: 'See it in motion' },
  trailerSub: {
    ar: 'إعلان قصير يشرح الفكرة في عشر ثوانٍ: اقفز، ضاعف رهانك، واسحب قبل فوات الأوان.',
    en: 'A ten-second teaser: hop, multiply, and cash out before it is too late.',
  },
  posterAlt: { ar: 'ملصق لعبة Chicken Road', en: 'Chicken Road poster' },

  howTitle: { ar: 'ثلاث خطوات فقط', en: 'Just three steps' },
  step1t: { ar: 'حدّد رهانك والصعوبة', en: 'Set your bet and difficulty' },
  step1p: {
    ar: 'من $0.10 إلى $200، مع أربعة مستويات خطورة تغيّر عدد السيارات على الطريق.',
    en: 'From $0.10 to $200, with four risk levels that change how many cars patrol the road.',
  },
  step2t: { ar: 'تقدّم خطوة بخطوة', en: 'Advance step by step' },
  step2p: {
    ar: 'كل قفزة تنقل الدجاجة إلى غطاء جديد، وكل غطاء يرفع المضاعف.',
    en: 'Every hop lands the chicken on a new manhole, and every manhole raises the multiplier.',
  },
  step3t: { ar: 'اسحب قبل الاصطدام', en: 'Cash out before the hit' },
  step3p: {
    ar: 'تحصل على رهانك مضروباً بالمضاعف الحالي، وإذا مرّت سيارة قبل أن تسحب خسرت الرهان.',
    en: 'You take your bet times the current multiplier. If a car gets there first, the bet is gone.',
  },

  levelsTitle: { ar: 'اختر مستوى المخاطرة', en: 'Pick your risk level' },
  levelsSub: {
    ar: 'كلما زادت السيارات كبرت المضاعفات. اضغط على أي مستوى لمعاينة طريقه.',
    en: 'More cars means bigger multipliers. Tap a level to preview its road.',
  },
  levelsAria: { ar: 'مستويات الصعوبة', en: 'Difficulty levels' },
  lvEasy: { ar: 'سهل', en: 'Easy' },
  lvMedium: { ar: 'متوسط', en: 'Medium' },
  lvHard: { ar: 'صعب', en: 'Hard' },
  lvHardcore: { ar: 'هاردكور', en: 'Hardcore' },
  lvSteps: { ar: 'الخطوات', en: 'Steps' },
  lvCars: { ar: 'السيارات', en: 'Cars' },
  cars1: { ar: '1 من 25', en: '1 of 25' },
  cars3: { ar: '3 من 25', en: '3 of 25' },
  cars5: { ar: '5 من 25', en: '5 of 25' },
  cars10: { ar: '10 من 25', en: '10 of 25' },
  risk1: { ar: 'مخاطرة منخفضة', en: 'Low risk' },
  risk2: { ar: 'مخاطرة متوسطة', en: 'Medium risk' },
  risk3: { ar: 'مخاطرة عالية', en: 'High risk' },
  risk4: { ar: 'أعلى مخاطرة', en: 'Maximum risk' },
  previewLabel: { ar: 'السيارات المخفية على الطريق:', en: 'Hidden cars on the road:' },
  thLevel: { ar: 'المستوى', en: 'Level' },
  thSteps: { ar: 'الخطوات', en: 'Steps' },
  thCars: { ar: 'السيارات', en: 'Cars' },
  thMax: { ar: 'أقصى مضاعف', en: 'Max multiplier' },
  levelsNote: {
    ar: 'الأرقام الكبيرة سقف نظري نادر جداً، وليست هدفاً لكل جولة.',
    en: 'The big numbers are a rare theoretical ceiling, not a per-round target.',
  },

  whyTitle: { ar: 'ما الذي يميّز Chicken Road؟', en: 'Why Chicken Road is different' },
  why1t: { ar: 'قرارك يصنع النتيجة', en: 'Your decision shapes the round' },
  why1p: {
    ar: 'ليست لعبة تكتفي بمشاهدتها؛ كل خطوة قرار: إما أن تتقدّم فترفع المضاعف، أو تسحب فتثبّت ربحك.',
    en: 'Not a slot you watch. Every step is a call: push on and raise the multiplier, or cash out and lock it.',
  },
  why2t: { ar: 'سحب في أي لحظة', en: 'Cash out any moment' },
  why2p: {
    ar: 'زر السحب بين يديك طوال الجولة، ولحظة الضغط عليه تحصل فوراً على رهانك مضروباً بالمضاعف الحالي.',
    en: 'The cash-out button is yours the whole round. The moment you press it, you take your bet times the current multiplier.',
  },
  why3t: { ar: 'شفافية رياضية', en: 'Mathematical transparency' },
  why3p: {
    ar: 'لكل جولة بصمة مشفّرة (SHA-256) تُحسب قبل انطلاقها، لتتأكد بنفسك أن النتيجة لم تتغيّر بعد رهانك.',
    en: 'Every round gets a SHA-256 fingerprint computed before it starts, so you can verify nothing changed after your bet.',
  },
  rtpNote: {
    ar: 'العائد النظري للاعب في جميع المستويات. الهامش معلن في المعادلات وليس مخفياً.',
    en: 'Theoretical return to player on every level. The edge is stated in the math, not hidden.',
  },
  fairText: {
    ar: 'النتائج عشوائية بالكامل ولا توجد أي استراتيجية مضمونة. العب للمتعة وحدّد ميزانيتك مسبقاً.',
    en: 'Outcomes are fully random and no strategy is guaranteed. Play for fun and set a budget in advance.',
  },

  featTitle: { ar: 'كل ما تحتاجه في جولة واحدة', en: 'Everything you need in one round' },
  feat1p: { ar: 'جرّب من دون حساب أو بيانات', en: 'Try it with no account, no details' },
  feat2p: { ar: '10,000$ تتجدد تلقائياً', en: '$10,000 that refills itself' },
  feat3p: { ar: 'من السهل إلى الهاردكور', en: 'From Easy to Hardcore' },
  feat4t: { ar: 'مضاعفات متصاعدة', en: 'Climbing multipliers' },
  feat4p: { ar: 'كل خطوة ترفع السقف', en: 'Every step raises the ceiling' },
  feat5t: { ar: 'تحكّم كامل بالسحب', en: 'Cash-out control' },
  feat5p: { ar: 'القرار بيدك في كل لحظة', en: 'The call is yours, always' },
  feat6t: { ar: 'جولات سريعة', en: 'Fast rounds' },
  feat6p: { ar: 'جولة كاملة في ثوانٍ', en: 'A full round in seconds' },

  faqTitle: { ar: 'أسئلة شائعة', en: 'Quick questions' },
  q1: { ar: 'هل النسخة التجريبية مجانية فعلاً؟', en: 'Is the demo really free?' },
  a1: {
    ar: 'نعم. رصيد تجريبي يتجدد تلقائياً، من دون تسجيل أو أي بيانات.',
    en: 'Yes. A practice balance that refills itself, and we ask for no sign-up and no details.',
  },
  q2: { ar: 'ما الفرق بين التجريبي واللعب الحقيقي؟', en: 'Demo vs real play, what changes?' },
  a2: {
    ar: 'اللعبة نفسها والأرقام نفسها. الفرق أن اللعب الحقيقي يجري بمال فعلي على منصة الشريك ويتطلب إنشاء حساب هناك.',
    en: 'Same game, same numbers. Real play happens with real money on the partner platform and requires an account there.',
  },
  q3: { ar: 'هل توجد استراتيجية مضمونة؟', en: 'Is there a guaranteed strategy?' },
  a3: {
    ar: 'لا. اللعبة عشوائية والهامش قائم في المعادلات مهما فعلت. حدّد ميزانية والتزم بها، وعامل أي ربح كمكافأة لا كخطة.',
    en: 'No. The game is random and the house edge is in the math no matter what. Set a budget, stick to it, and treat any win as a bonus, not a plan.',
  },
  q4: { ar: 'هل تعمل على الجوال؟', en: 'Does it work on mobile?' },
  a4: {
    ar: 'نعم، اللعبة والموقع مصمّمان للجوال والحاسوب معاً. ولتجربة أوسع افتح اللعبة بملء الشاشة.',
    en: 'Yes, the game and the site are built for phones and desktops. For a bigger view, open the game full screen.',
  },
  q5: { ar: 'كيف ألعب بمسؤولية؟', en: 'How do I play responsibly?' },
  a5: {
    ar: 'حدّد ميزانية قبل البدء وتوقّف عند بلوغها، وتعامل مع اللعب كترفيه لا كمصدر دخل. اللعب الحقيقي للبالغين فقط (+18).',
    en: 'Set a budget before you start and stop when you reach it. Treat play as entertainment, not income. Real play is for adults only (18+).',
  },

  ctaTitle: { ar: 'جاهز لعبور الطريق؟', en: 'Ready to cross?' },
  ctaNote: {
    ar: 'اللعب بمال حقيقي للبالغين فقط. حدّد ميزانيتك مسبقاً والعب بمسؤولية.',
    en: 'Real-money play is for adults only. Set a budget in advance and play responsibly.',
  },

  footerAbout: {
    ar: 'لعبة كراش سريعة بقرارات حقيقية: كل خط يرفع مضاعفك، وسيارة واحدة تنهي الجولة. جرّبها مجاناً قبل أن تقرر.',
    en: 'A fast crash game of real decisions: every lane raises your multiplier, one car ends the round. Try it free before you decide.',
  },
  footerLinks: { ar: 'روابط', en: 'Links' },
  footerPrivacy: { ar: 'سياسة الخصوصية', en: 'Privacy Policy' },
  footerTerms: { ar: 'الشروط والأحكام', en: 'Terms & Conditions' },
  footerMain: {
    ar: 'القمار للبالغين فقط وقد يؤدي إلى خسارة أموالك. العب بمسؤولية، حدّد ميزانيتك مسبقاً، وتوقف عند بلوغها.',
    en: 'Gambling is for adults only and can lose you money. Play responsibly, set a budget in advance, and stop when you reach it.',
  },
  footerDim: {
    ar: 'اللعب بمال حقيقي يتم على منصة شريك خارجية مسؤولة عن الحسابات والمدفوعات. هذه الصفحة تقدم نسخة تجريبية مجانية للتعريف باللعبة.',
    en: 'Real-money play happens on an external partner platform responsible for accounts and payments. This page offers a free demo to introduce the game.',
  },
  copyright: { ar: '© 2026 Chicken Road. جميع الحقوق محفوظة.', en: '© 2026 Chicken Road. All rights reserved.' },
};

function applyLang(lang) {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  for (const [key, val] of Object.entries(I18N)) {
    document.querySelectorAll(`[data-i18n="${key}"]`).forEach((n) => (n.innerHTML = val[lang]));
    document.querySelectorAll(`[data-i18n-content="${key}"]`).forEach((n) => n.setAttribute('content', val[lang]));
    document.querySelectorAll(`[data-i18n-attr-aria-label="${key}"]`).forEach((n) => n.setAttribute('aria-label', val[lang]));
    document.querySelectorAll(`[data-i18n-attr-alt="${key}"]`).forEach((n) => n.setAttribute('alt', val[lang]));
  }
  // per-country bonus copy (overrides the generic i18n text)
  if (CONFIG.bonus) {
    const b = CONFIG.bonus;
    const set = (sel, html) => {
      if (html?.[lang] == null) return;
      document.querySelectorAll(sel).forEach((el) => (el.innerHTML = html[lang]));
    };
    set('.bonus-tag', b.tag);
    set('.bonus-title, .js-bonus-title', b.title);
    set('.bonus-sub', b.sub);
    set('.btn-bonus', b.cta);
    set('.bonus-terms', b.terms);
  }

  document.title = I18N.title[lang];
  document.getElementById('hero-canvas')?.setAttribute('aria-label', I18N.heroAlt[lang]);
  document.getElementById('demo-iframe')?.setAttribute('title', I18N.iframeTitle[lang]);
  document.querySelectorAll('.lang-btn').forEach((b) => (b.textContent = lang === 'ar' ? 'EN' : 'عربي'));

  // the teaser video exists in both languages
  const vid = document.getElementById('trailer-vid');
  if (vid) {
    const want = lang === 'ar' ? '/media/trailer-ar.mp4' : '/media/trailer-en.mp4';
    if (!vid.src.endsWith(want)) {
      vid.src = want;
      vid.load();
      vid.play?.().catch(() => {});
    }
  }
  syncPreview();
}

function toggleLang() {
  const next = getLang() === 'ar' ? 'en' : 'ar';
  setLang(next);
  applyLang(next);
  const frame = document.getElementById('demo-iframe');
  // the game reads the language at boot; reload only if it was loaded inline
  if (frame && frame.getAttribute('src')) frame.src = frame.getAttribute('src');
}

/* ================= storage helpers ================= */

function setStorage(key, value, days = 30) {
  try {
    localStorage.setItem(key, JSON.stringify({ value, expiry: Date.now() + days * 864e5 }));
  } catch { /* storage unavailable: we simply re-ask next visit */ }
}
function getStorage(key) {
  try {
    const data = localStorage.getItem(key);
    if (!data) return null;
    const parsed = JSON.parse(data);
    if (Date.now() > parsed.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

/* ================= sprites into the DOM ================= */

const sprites = buildSprites();
const SPRITE_MAP = {
  chicken: sprites.chicken,
  chickenHop: sprites.chickenHop,
  coin: sprites.coin,
  manhole: sprites.manhole,
  barrier: sprites.barrier,
  car: sprites.cars[3],   // coral red
  car2: sprites.cars[1],  // sky blue
};
{
  const urls = {};
  document.querySelectorAll('[data-sprite]').forEach((img) => {
    const key = img.dataset.sprite;
    if (!SPRITE_MAP[key]) return;
    urls[key] ||= SPRITE_MAP[key].toDataURL();
    img.src = urls[key];
  });
}

/* ================= hero road canvas ================= */

const LANE_W = 150;
const cv = document.getElementById('hero-canvas');
const ctx = cv.getContext('2d');
const dpr = Math.min(2, window.devicePixelRatio || 1);
const LADDER = ['1.63x', '2.80x', '4.95x', '9.08x', '17.34x', '34.68x', '73.21x', '164.7x', '363x', '847x'];

let W = 0, H = 0, ROW_Y = 0, rtl = true;

function sizeCanvas() {
  W = cv.clientWidth;
  H = cv.clientHeight;
  cv.width = Math.ceil(W * dpr);
  cv.height = Math.ceil(H * dpr);
  ROW_Y = H * 0.52;
  rtl = document.documentElement.dir === 'rtl';
}
sizeCanvas();

const laneX = (k) => (rtl ? W - (k + 0.5) * LANE_W : (k + 0.5) * LANE_W);

const put = (spr, cx, cy, sx = 1, sy = 1) => {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(sx, sy);
  ctx.drawImage(spr, -spr.logicalW / 2, -spr.logicalH / 2, spr.logicalW, spr.logicalH);
  ctx.restore();
};

function manholeText(x, y, label) {
  ctx.font = `800 24px Alexandria, 'Arial Black', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(24,26,31,0.9)';
  ctx.fillText(label, x, y + 1.5);
  ctx.fillStyle = '#d3d7de';
  ctx.fillText(label, x, y);
}

const car = { y: 0, speed: 430, lane: 3, spr: sprites.cars[4], wait: 1.5 };
const pickCar = () => {
  const r = Math.random();
  if (r < 0.3) return sprites.adVan; // sponsor livery on ~30% of traffic
  if (r < 0.42) return sprites.fireTruck;
  return sprites.cars[(Math.random() * sprites.cars.length) | 0];
};

// gentle hop-in-place cycle for the hero chicken
const hop = { t: 0, period: 3.4, dur: 0.5 };
let blinkAt = 2.2, blinking = 0;

function draw(t, dt) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);

  const lanes = Math.ceil(W / LANE_W) + 1;
  for (let k = 0; k < lanes; k++) {
    const x = rtl ? W - (k + 1) * LANE_W : k * LANE_W;
    ctx.drawImage(sprites.laneStrip, x, ROW_Y - 380, LANE_W, 720);
  }

  // passed lane behind the chicken: coin + barrier
  put(sprites.coin, laneX(0), ROW_Y);
  put(sprites.barrier, laneX(0), ROW_Y - 120);

  // the chicken stands ON its own manhole, feet on the ground
  put(sprites.manhole, laneX(1), ROW_Y);

  // multiplier manholes ahead
  for (let k = 2; k < lanes; k++) {
    put(sprites.manhole, laneX(k), ROW_Y);
    if (LADDER[k - 2]) manholeText(laneX(k), ROW_Y, LADDER[k - 2]);
  }

  // ambient car on a lane ahead
  if (car.wait > 0) {
    car.wait -= dt;
    if (car.wait <= 0) {
      car.y = -170;
      car.lane = 2 + ((Math.random() * Math.max(1, lanes - 3)) | 0);
      car.spr = pickCar();
      car.speed = 380 + Math.random() * 260;
      // the sponsor van cruises slowly so the ad stays readable
      if (car.spr === sprites.adVan) car.speed *= 0.55;
    }
  } else {
    car.y += car.speed * dt;
    if (car.y > H + 180) car.wait = 2 + Math.random() * 3;
  }
  if (car.wait <= 0) {
    const cx = laneX(car.lane);
    ctx.fillStyle = 'rgba(18,20,26,0.22)';
    ctx.beginPath();
    ctx.ellipse(cx + 5, car.y + 8, car.spr.logicalW * 0.42, car.spr.logicalH * 0.46, 0, 0, Math.PI * 2);
    ctx.fill();
    put(car.spr, cx, car.y);
  }

  // hero chicken hopping gently in place on its manhole
  blinkAt -= dt;
  if (blinkAt <= 0) { blinking = 0.13; blinkAt = 2.2 + Math.random() * 2.4; }
  if (blinking > 0) blinking -= dt;

  hop.t = (hop.t + dt) % hop.period;
  const hp = hop.t < hop.dur ? hop.t / hop.dur : -1;
  let hopY = 0, sx = 1, sy = 1, spr = blinking > 0 ? sprites.chickenBlink : sprites.chicken;
  if (hp >= 0) {
    hopY = -26 * Math.sin(Math.PI * hp);
    spr = sprites.chickenHop;
    sx = 0.96;
    sy = 1.06;
  } else {
    hopY = Math.sin(t * 3.4) * 2;
  }
  const footY = ROW_Y + 44;
  const chx = laneX(1);
  const air = hp >= 0 ? Math.sin(Math.PI * hp) : 0;
  ctx.fillStyle = `rgba(18,20,26,${0.28 * (1 - 0.45 * air)})`;
  ctx.beginPath();
  ctx.ellipse(chx, footY + 2, 46 * (1 - 0.3 * air), 13 * (1 - 0.3 * air), 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.save();
  ctx.translate(chx, footY + hopY);
  ctx.scale(sx, sy);
  ctx.drawImage(spr, -spr.logicalW / 2, -spr.logicalH + 8, spr.logicalW, spr.logicalH);
  ctx.restore();

  // blend the top of the road into the pale sky
  const fade = ctx.createLinearGradient(0, 0, 0, 64);
  fade.addColorStop(0, 'rgba(244,250,255,1)');
  fade.addColorStop(1, 'rgba(244,250,255,0)');
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, W, 64);
}

draw(0.001, 0.016);

if (!reduce) {
  let last = performance.now();
  let running = true;
  const loop = () => {
    const now = performance.now();
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    if (running && !document.body.classList.contains('game-open')) draw(now / 1000, dt);
    if (document.visibilityState === 'hidden') setTimeout(loop, 66);
    else requestAnimationFrame(loop);
  };
  new IntersectionObserver(([e]) => { running = e.isIntersecting; }, { threshold: 0.02 }).observe(cv);
  loop();
}

let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => { sizeCanvas(); draw(0.001, 0.016); }, 120);
});

/* ================= nav ================= */

const nav = document.getElementById('site-nav');
const burger = document.getElementById('nav-burger');

window.addEventListener('scroll', () => {
  nav.classList.toggle('scrolled', window.scrollY > 8);
}, { passive: true });

burger.addEventListener('click', () => {
  const open = nav.classList.toggle('menu-open');
  burger.setAttribute('aria-expanded', String(open));
});
document.querySelectorAll('.nav-mobile a').forEach((a) =>
  a.addEventListener('click', () => {
    nav.classList.remove('menu-open');
    burger.setAttribute('aria-expanded', 'false');
  })
);

// active section indicator
{
  const links = [...document.querySelectorAll('.nav-links a')];
  const byId = Object.fromEntries(links.map((a) => [a.getAttribute('href').slice(1), a]));
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          links.forEach((l) => l.classList.remove('active'));
          byId[e.target.id]?.classList.add('active');
        }
      }
    },
    { rootMargin: '-30% 0px -55% 0px' }
  );
  ['demo', 'how', 'levels', 'faq'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) io.observe(el);
  });
}

/* ================= inline game (desktop) ================= */

const isPhone = () => window.matchMedia('(max-width: 860px)').matches;

{
  const frame = document.getElementById('demo-iframe');
  const loader = document.getElementById('iframe-loader');
  frame.addEventListener('load', () => (loader.style.display = 'none'));
  const loadInline = () => {
    if (!isPhone() && !frame.getAttribute('src')) frame.src = frame.dataset.src;
  };
  // phones never load the inline game (they use the fullscreen launcher),
  // but if the window later widens past the breakpoint, load it once
  loadInline();
  window.addEventListener('resize', loadInline);
}

/* ================= fullscreen game overlay (mobile) ================= */

let gameOverlay = null;

function closeGame() {
  if (!gameOverlay) return;
  gameOverlay.remove();
  gameOverlay = null;
  document.documentElement.style.overflow = '';
  // resume the landing animations that were frozen while playing
  document.body.classList.remove('game-open');
  document.getElementById('trailer-vid')?.play?.().catch(() => {});
}

function openGame() {
  if (gameOverlay) return;
  const lang = getLang();
  gameOverlay = document.createElement('div');
  gameOverlay.className = 'game-overlay';
  gameOverlay.innerHTML = `
    <iframe src="/play.html" title="${I18N.iframeTitle[lang]}" allow="autoplay"></iframe>
    <button class="game-back">✕ <span>${I18N.back[lang]}</span></button>
    <div class="rotate-hint">${I18N.rotateHint[lang]}</div>`;
  document.body.appendChild(gameOverlay);
  document.documentElement.style.overflow = 'hidden';
  // freeze every landing animation so the game gets the whole frame budget
  document.body.classList.add('game-open');
  document.getElementById('trailer-vid')?.pause?.();

  // the browser/OS back gesture closes the game instead of leaving the page
  history.pushState({ game: 1 }, '');
  gameOverlay.querySelector('.game-back').addEventListener('click', () => history.back());

  const hint = gameOverlay.querySelector('.rotate-hint');
  if (window.matchMedia('(orientation: portrait)').matches) {
    setTimeout(() => hint.classList.add('gone'), 3800);
  } else {
    hint.remove();
  }
}

window.addEventListener('popstate', closeGame);
document.getElementById('game-launch')?.addEventListener('click', openGame);

/* ================= trailer: play only while visible ================= */

{
  const vid = document.getElementById('trailer-vid');
  if (vid && !reduce && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !document.body.classList.contains('game-open')) vid.play().catch(() => {});
        else vid.pause();
      },
      { threshold: 0.4 }
    ).observe(vid);
  }

  // the teaser plays its sound ONCE per visit (browsers require a user
  // gesture to unmute), then keeps looping silently
  if (vid && !sessionStorage.getItem('trailerSoundDone')) {
    const inView = () => {
      const r = vid.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight && r.height > 0;
    };
    const arm = () => window.addEventListener('pointerdown', kick, { once: true });
    const kick = () => {
      // only claim the gesture while the video is on screen; otherwise re-arm
      if (!inView() || document.body.classList.contains('game-open')) return arm();
      sessionStorage.setItem('trailerSoundDone', '1');
      vid.muted = false;
      vid.volume = 0.9;
      vid.loop = false;
      vid.currentTime = 0;
      const hush = () => {
        vid.muted = true;
        vid.loop = true;
        vid.play().catch(() => {});
      };
      vid.addEventListener('ended', hush, { once: true });
      vid.play().catch(hush);
    };
    arm();
  }
}

/* ================= FAQ accordion ================= */

document.querySelectorAll('.faq-item').forEach((item) => {
  const btn = item.querySelector('.faq-q');
  btn.addEventListener('click', () => {
    const open = item.classList.toggle('open');
    btn.setAttribute('aria-expanded', String(open));
  });
});

/* ================= difficulty selector ================= */

const previewSlots = document.getElementById('preview-slots');
const previewName = document.getElementById('preview-name');
const previewCount = document.getElementById('preview-count');
let selectedLevel = 'easy';

function renderSlots(k) {
  const total = 25;
  const carIdx = new Set();
  while (carIdx.size < k) carIdx.add((Math.random() * total) | 0);
  previewSlots.innerHTML = Array.from({ length: total }, (_, i) =>
    `<i class="${carIdx.has(i) ? 'car' : ''}"></i>`
  ).join('');
  previewCount.textContent = `${k} / ${total}`;
}

function syncPreview() {
  const lang = getLang();
  const key = { easy: 'lvEasy', medium: 'lvMedium', hard: 'lvHard', hardcore: 'lvHardcore' }[selectedLevel];
  if (previewName && I18N[key]) previewName.textContent = I18N[key][lang];
}

document.querySelectorAll('.level-card').forEach((card) =>
  card.addEventListener('click', () => {
    document.querySelectorAll('.level-card').forEach((c) => c.setAttribute('aria-pressed', 'false'));
    card.setAttribute('aria-pressed', 'true');
    selectedLevel = card.dataset.level;
    renderSlots(Number(card.dataset.k));
    syncPreview();
  })
);
renderSlots(1);

/* ================= per-country affiliate links ================= */

document.querySelectorAll('[data-aff]').forEach((a) => (a.href = AFFILIATE_LINK));

/* ================= language toggles ================= */

applyLang(getLang());
document.getElementById('lang-toggle')?.addEventListener('click', toggleLang);
document.getElementById('lang-toggle-2')?.addEventListener('click', toggleLang);

/* ============ pixels + optional bridge on real-money CTA clicks ============ */

{
  const bridge = CONFIG.bridgePage ? document.getElementById('bridge') : null;
  const bridgeGo = document.getElementById('bridge-continue');

  const closeBridge = () => {
    if (!bridge) return;
    bridge.hidden = true;
    document.documentElement.style.overflow = '';
  };

  document.querySelectorAll('a[href*="time4100.com"]').forEach((a, i) => {
    // the bridge's own "continue" button must never be intercepted
    if (a.id === 'bridge-continue') return;
    a.addEventListener('click', (e) => {
      const category = `landing_${a.closest('section,header,footer')?.id || a.closest('section,header,footer')?.className.split(' ')[0] || i}`;
      window.fbq?.('track', 'Lead', { content_name: 'real_money_cta', content_category: category });
      window.ttq?.track('ClickButton', { content_name: 'real_money_cta', content_category: category });
      // show the expectation-setting screen instead of jumping straight out
      if (bridge && bridgeGo) {
        e.preventDefault();
        bridgeGo.href = a.href;
        bridge.hidden = false;
        document.documentElement.style.overflow = 'hidden';
      }
    });
  });

  if (bridge) {
    bridgeGo?.addEventListener('click', () => setTimeout(closeBridge, 60));
    document.getElementById('bridge-back')?.addEventListener('click', closeBridge);
    bridge.addEventListener('click', (e) => {
      if (e.target === bridge) closeBridge();
    });
  }
}

/* ================= scroll reveals ================= */

if (!reduce && 'IntersectionObserver' in window) {
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      }
    },
    { threshold: 0.12 }
  );
  document.querySelectorAll('.reveal').forEach((n) => io.observe(n));
} else {
  document.querySelectorAll('.reveal').forEach((n) => n.classList.add('in'));
}

/* ================= v4: AOS entrance animations ================= */

if (window.AOS) {
  window.AOS.init({
    once: true,
    duration: 900,
    easing: 'ease-out-cubic',
    offset: 60,
    disable: reduce,
  });
}

/* ================= v4: pause the marquee while it is off screen ================= */

{
  const mq = document.querySelector('.marquee');
  if (mq && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      ([e]) => mq.classList.toggle('off', !e.isIntersecting),
      { threshold: 0 }
    ).observe(mq);
  }
}

/* ================= v4: animated stat counters ================= */

{
  const nums = document.querySelectorAll('.stat-num[data-count]');
  const fmt = (v) => Math.round(v).toLocaleString('en-US');

  const animate = (el) => {
    const target = parseFloat(el.dataset.count);
    const prefix = el.dataset.prefix || '';
    const suffix = el.dataset.suffix || '';
    if (reduce) {
      el.textContent = prefix + fmt(target) + suffix;
      return;
    }
    const t0 = performance.now();
    const DUR = 1600;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / DUR);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = prefix + fmt(target * eased) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  if (nums.length) {
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              animate(e.target);
              io.unobserve(e.target);
            }
          }
        },
        { threshold: 0.6 }
      );
      nums.forEach((n) => io.observe(n));
    } else {
      nums.forEach(animate);
    }
  }
}
